import { call, put, takeEvery, takeLatest, select, all, delay } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  toolFixtureApi,
  SearchToolFixturesRequest,
  SearchToolFixturesResponse,
  CreateToolFixtureRequest,
  UpdateToolFixtureRequest,
  CreateCalibrationRequest,
  UpdateCalibrationRequest,
  ToolFixture,
  CalibrationCalendarDetail,
  Supplier,
  Department,
  ToolType,
  // ToolFixtureStatistics, // REMOVED - no longer needed
} from '../../services/toolFixtureApi';
import {
  fetchToolFixturesStart,
  fetchToolFixturesSuccess,
  fetchToolFixturesFailure,
  fetchToolFixtureByIdStart,
  fetchToolFixtureByIdSuccess,
  fetchToolFixtureByIdFailure,
  createToolFixtureStart,
  createToolFixtureSuccess,
  createToolFixtureFailure,
  updateToolFixtureStart,
  updateToolFixtureSuccess,
  updateToolFixtureFailure,
  deleteToolFixtureStart,
  deleteToolFixtureSuccess,
  deleteToolFixtureFailure,
  fetchCalibrationEventsStart,
  fetchCalibrationEventsSuccess,
  fetchCalibrationEventsFailure,
  createCalibrationStart,
  createCalibrationSuccess,
  createCalibrationFailure,
  updateCalibrationStart,
  updateCalibrationSuccess,
  updateCalibrationFailure,
  deleteCalibrationStart,
  deleteCalibrationSuccess,
  deleteCalibrationFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  // fetchStatisticsStart, // REMOVED - no longer needed
  // fetchStatisticsSuccess, // REMOVED - no longer needed
  // fetchStatisticsFailure, // REMOVED - no longer needed
} from '../reducers/toolFixtureSlice';
import { RootState } from '../index';

// Tool Fixtures API Sagas
function* fetchToolFixturesApi(action: PayloadAction<SearchToolFixturesRequest | undefined>): Generator<any, void, any> {
  try {
    console.log('[ToolFixtureSaga] fetchToolFixturesApi started');
    
    const state: RootState = yield select();
    const { toolFixture } = state;
    
    const params: SearchToolFixturesRequest = {
      pageNum: toolFixture.pagination.pageNum,
      pageSize: toolFixture.pagination.pageSize,
      filter: {
        ...toolFixture.filters,
        ...(toolFixture.searchTerm && { TFNum: toolFixture.searchTerm, Description: toolFixture.searchTerm }),
      },
      ...action.payload,
    };

    console.log('[ToolFixtureSaga] Calling toolFixtureApi.getAll with params:', params);
    console.log('[ToolFixtureSaga] API endpoint: POST /toolfixture/search');
    
    const response: SearchToolFixturesResponse = yield call(toolFixtureApi.getAll, params);
    
    console.log('[ToolFixtureSaga] toolFixtureApi.getAll response received:', {
      success: true,
      responseType: typeof response,
      hasItems: !!response?.items,
      itemsLength: response?.items?.length || 0,
      totalCount: response?.totalCount,
      pageNum: response?.pageNum,
      pageSize: response?.pageSize
    });
    
    yield put(fetchToolFixturesSuccess(response));
    console.log('[ToolFixtureSaga] fetchToolFixturesSuccess dispatched');
  } catch (error: any) {
    console.error('[ToolFixtureSaga] fetchToolFixturesApi error:', {
      error: error,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      timestamp: new Date().toISOString()
    });
    
    yield put(fetchToolFixturesFailure(error.message || 'Failed to fetch tool fixtures'));
  }
}

function* fetchToolFixtureByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const toolFixture: ToolFixture = yield call(toolFixtureApi.getById, action.payload);
    yield put(fetchToolFixtureByIdSuccess(toolFixture));
  } catch (error: any) {
    yield put(fetchToolFixtureByIdFailure(error.message || 'Failed to fetch tool fixture'));
  }
}

function* createToolFixtureApi(action: PayloadAction<CreateToolFixtureRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = toolFixtureApi.validateToolFixture(action.payload);
    if (validationErrors.length > 0) {
      yield put(createToolFixtureFailure(validationErrors.join(', ')));
      return;
    }

    const toolFixture: ToolFixture = yield call(toolFixtureApi.create, action.payload);
    yield put(createToolFixtureSuccess(toolFixture));
    
    // Refresh statistics after creation
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(createToolFixtureFailure(error.message || 'Failed to create tool fixture'));
  }
}

function* updateToolFixtureApi(action: PayloadAction<UpdateToolFixtureRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = toolFixtureApi.validateToolFixture(action.payload);
    if (validationErrors.length > 0) {
      yield put(updateToolFixtureFailure(validationErrors.join(', ')));
      return;
    }

    const toolFixture: ToolFixture = yield call(toolFixtureApi.update, action.payload);
    yield put(updateToolFixtureSuccess(toolFixture));
    
    // Refresh statistics after update
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(updateToolFixtureFailure(error.message || 'Failed to update tool fixture'));
  }
}

function* deleteToolFixtureApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(toolFixtureApi.delete, action.payload);
    yield put(deleteToolFixtureSuccess(action.payload));
    
    // Refresh statistics after deletion
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(deleteToolFixtureFailure(error.message || 'Failed to delete tool fixture'));
  }
}

// Calibration Events API Sagas
function* fetchCalibrationEventsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const events: CalibrationCalendarDetail[] = yield call(toolFixtureApi.getCalibrationEvents, action.payload);
    yield put(fetchCalibrationEventsSuccess(events));
  } catch (error: any) {
    yield put(fetchCalibrationEventsFailure(error.message || 'Failed to fetch calibration events'));
  }
}

function* createCalibrationApi(action: PayloadAction<CreateCalibrationRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = toolFixtureApi.validateCalibrationEvent(action.payload);
    if (validationErrors.length > 0) {
      yield put(createCalibrationFailure(validationErrors.join(', ')));
      return;
    }

    const event: CalibrationCalendarDetail = yield call(toolFixtureApi.createCalibrationEvent, action.payload);
    yield put(createCalibrationSuccess(event));
    
    // Refresh statistics after creation
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(createCalibrationFailure(error.message || 'Failed to create calibration event'));
  }
}

function* updateCalibrationApi(action: PayloadAction<UpdateCalibrationRequest>): Generator<any, void, any> {
  try {
    // Validate before API call
    const validationErrors = toolFixtureApi.validateCalibrationEvent(action.payload);
    if (validationErrors.length > 0) {
      yield put(updateCalibrationFailure(validationErrors.join(', ')));
      return;
    }

    const event: CalibrationCalendarDetail = yield call(toolFixtureApi.updateCalibrationEvent, action.payload);
    yield put(updateCalibrationSuccess(event));
    
    // Refresh statistics after update
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(updateCalibrationFailure(error.message || 'Failed to update calibration event'));
  }
}

function* deleteCalibrationApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(toolFixtureApi.deleteCalibrationEvent, action.payload);
    yield put(deleteCalibrationSuccess(action.payload));
    
    // Refresh statistics after deletion
    // yield put(fetchStatisticsStart()); // REMOVED
  } catch (error: any) {
    yield put(deleteCalibrationFailure(error.message || 'Failed to delete calibration event'));
  }
}

// Dropdown Data Sagas
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    console.log('[ToolFixtureSaga] fetchDropdownsApi started');
    
    console.log('[ToolFixtureSaga] Calling toolFixtureApi.getSuppliers...');
    console.log('[ToolFixtureSaga] API endpoint: GET /supplier/');
    const suppliers: Supplier[] = yield call(toolFixtureApi.getSuppliers);
    console.log('[ToolFixtureSaga] getSuppliers response:', {
      success: true,
      suppliersLength: suppliers?.length || 0,
      suppliers: suppliers
    });
    
    console.log('[ToolFixtureSaga] Calling toolFixtureApi.getDepartments...');
    console.log('[ToolFixtureSaga] API endpoint: GET /department/');
    const departments: Department[] = yield call(toolFixtureApi.getDepartments);
    console.log('[ToolFixtureSaga] getDepartments response:', {
      success: true,
      departmentsLength: departments?.length || 0,
      departments: departments
    });
    
    console.log('[ToolFixtureSaga] Calling toolFixtureApi.getToolTypes...');
    console.log('[ToolFixtureSaga] API endpoint: GET /tooltype/');
    const toolTypes: ToolType[] = yield call(toolFixtureApi.getToolTypes);
    console.log('[ToolFixtureSaga] getToolTypes response:', {
      success: true,
      toolTypesLength: toolTypes?.length || 0,
      toolTypes: toolTypes
    });

    console.log('[ToolFixtureSaga] All dropdown calls successful, dispatching fetchDropdownsSuccess');
    yield put(fetchDropdownsSuccess({
      suppliers,
      departments,
      toolTypes,
    }));
    console.log('[ToolFixtureSaga] fetchDropdownsSuccess dispatched');
  } catch (error: any) {
    console.error('[ToolFixtureSaga] fetchDropdownsApi error:', {
      error: error,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      timestamp: new Date().toISOString()
    });
    
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Statistics Saga - REMOVED due to Lambda conversion errors
// function* fetchStatisticsApi(): Generator<any, void, any> {
//   try {
//     const statistics: ToolFixtureStatistics = yield call(toolFixtureApi.getStatistics);
//     yield put(fetchStatisticsSuccess(statistics));
//   } catch (error: any) {
//     yield put(fetchStatisticsFailure(error.message || 'Failed to fetch statistics'));
//   }
// }

// Watcher Sagas for UI interactions
function* watchSearchTerm(): Generator<any, void, any> {
  yield takeLatest('toolFixture/setSearchTerm', function* () {
    yield delay(500); // Debounce search
    yield put(fetchToolFixturesStart());
  });
}

function* watchFilters(): Generator<any, void, any> {
  yield takeLatest([
    'toolFixture/setCalibrationRequiredFilter',
    'toolFixture/setActiveFilter',
    'toolFixture/setSupplierFilter',
    'toolFixture/setDepartmentFilter',
    'toolFixture/setToolTypeFilter',
  ], function* () {
    yield put(fetchToolFixturesStart());
  });
}

function* watchPagination(): Generator<any, void, any> {
  yield takeLatest([
    'toolFixture/setPage',
    'toolFixture/setPageSize',
  ], function* () {
    yield put(fetchToolFixturesStart());
  });
}

// Root Tool Fixture Saga
export default function* toolFixtureSaga(): Generator<any, void, any> {
  yield all([
    // Main API calls
    takeEvery(fetchToolFixturesStart.type, fetchToolFixturesApi),
    takeEvery(fetchToolFixtureByIdStart.type, fetchToolFixtureByIdApi),
    takeEvery(createToolFixtureStart.type, createToolFixtureApi),
    takeEvery(updateToolFixtureStart.type, updateToolFixtureApi),
    takeEvery(deleteToolFixtureStart.type, deleteToolFixtureApi),
    
    // Calibration events
    takeEvery(fetchCalibrationEventsStart.type, fetchCalibrationEventsApi),
    takeEvery(createCalibrationStart.type, createCalibrationApi),
    takeEvery(updateCalibrationStart.type, updateCalibrationApi),
    takeEvery(deleteCalibrationStart.type, deleteCalibrationApi),
    
    // Dropdown data and statistics
    takeEvery(fetchDropdownsStart.type, fetchDropdownsApi),
    // takeEvery(fetchStatisticsStart.type, fetchStatisticsApi), // REMOVED
    
    // Watch for UI changes
    watchSearchTerm(),
    watchFilters(),
    watchPagination(),
  ]);
} 